import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Heart,
  Share2,
  Check,
  ArrowLeft,
  Bookmark,
  Sparkles,
  ChevronRight,
  NotebookPen,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  DBT_CATEGORIES,
  DBT_SKILLS,
  type DbtCategoryId,
  type DbtScenario,
  type DbtSkillCard,
  getSkillsForCategories,
} from "@/lib/dbt-skills-data";
import { useFavorite } from "@/hooks/useFavorite";
import { useDbtJournal, type DbtJournalEntry } from "@/hooks/useDbtJournal";

export const Route = createFileRoute("/dbt-skills")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "DBT Skills Cards — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Swipeable DBT skills: STOP, TIPP, DEAR MAN, Radical Acceptance, Wise Mind, Opposite Action, Self-Soothe, and Distress Tolerance — with a gentle practice for each.",
      },
      { property: "og:title", content: "DBT Skills Cards — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Trauma-informed DBT skill cards with a small practice you can try right now.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/dbt-skills" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/dbt-skills" }],
  }),
  component: withMemberGate(DbtSkillsPage, "DBT Skills"),
});

const FAV_KEY = "rt:fav-dbt-skills";

function DbtSkillsPage() {
  const [selected, setSelected] = useState<DbtCategoryId[]>(() =>
    DBT_CATEGORIES.map((c) => c.id),
  );
  const [deck, setDeck] = useState<DbtSkillCard[]>(() =>
    getSkillsForCategories(DBT_CATEGORIES.map((c) => c.id)),
  );
  const [index, setIndex] = useState(0);
  const [journalOpen, setJournalOpen] = useState(false);
  const { entries: journalEntries } = useDbtJournal();

  useEffect(() => {
    setDeck(getSkillsForCategories(selected));
    setIndex(0);
  }, [selected]);

  const card = deck[index];
  const nextCard = deck[(index + 1) % Math.max(deck.length, 1)];

  const toggleCategory = (id: DbtCategoryId) => {
    setSelected((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev;
      return has ? prev.filter((c) => c !== id) : [...prev, id];
    });
  };

  const advance = useCallback(() => {
    setIndex((i) => (deck.length ? (i + 1) % deck.length : 0));
  }, [deck.length]);

  return (
    <div className="min-h-dvh bg-[var(--bg-soft,theme(colors.background))]">
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Daily Support
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setJournalOpen(true)}
              className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/40 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)] hover:bg-gold-light/40"
            >
              <NotebookPen className="h-3.5 w-3.5" />
              Journals
              {journalEntries.length > 0 && (
                <span className="ms-0.5 rounded-full bg-[var(--gold-deep)] px-1.5 text-[10px] font-semibold text-background">
                  {journalEntries.length}
                </span>
              )}
            </button>
            <Link
              to="/favorites"
              className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)]"
            >
              <Bookmark className="h-3.5 w-3.5" />
              Saved
            </Link>
          </div>
        </div>

        {journalOpen && (
          <SavedJournalsDrawer onClose={() => setJournalOpen(false)} />
        )}


        <header className="mb-6 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            Daily Support
          </p>
          <h1 className="mt-2 font-display text-3xl text-foreground sm:text-4xl">
            🧠 DBT Skills Cards
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground/70">
            Eight gentle skills for big feelings. Swipe through, save the ones
            that meet you today, and try one small practice in the moment.
          </p>
        </header>

        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {DBT_CATEGORIES.map((c) => {
            const on = selected.includes(c.id);
            const count = DBT_SKILLS.filter((s) => s.category === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleCategory(c.id)}
                aria-pressed={on}
                className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  on
                    ? "border-transparent bg-foreground text-background"
                    : "border-border bg-card text-foreground/70 hover:text-foreground"
                }`}
              >
                <span aria-hidden>{c.emoji}</span>
                {c.label}
                <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>

        <Deck card={card} nextCard={nextCard} onSwiped={advance} />

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={advance}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-cream/40 px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-gold-light/40"
          >
            Next skill
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-8 text-center text-[11px] text-foreground/50">
          {deck.length} skills · drag a card or tap Next
        </p>
      </div>
    </div>
  );
}

function Deck({
  card,
  nextCard,
  onSwiped,
}: {
  card: DbtSkillCard | undefined;
  nextCard: DbtSkillCard | undefined;
  onSwiped: () => void;
}) {
  if (!card) {
    return (
      <div className="rounded-3xl border border-border bg-card p-10 text-center text-sm text-foreground/60">
        No skills in this selection.
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full max-w-md">
      {nextCard && nextCard.id !== card.id && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-3 top-3 bottom-[-12px] -z-0 rounded-[28px] border border-gold/20 bg-gradient-to-br from-gold-light/30 via-card to-cream/50 opacity-70 shadow-[var(--shadow-gold)]"
        />
      )}
      <SwipeableCard key={card.id} card={card} onSwiped={onSwiped} />
    </div>
  );
}

function SwipeableCard({
  card,
  onSwiped,
}: {
  card: DbtSkillCard;
  onSwiped: () => void;
}) {
  const { saved, toggle } = useFavorite(FAV_KEY, card.id);

  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [leaving, setLeaving] = useState<null | "left" | "right">(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const elRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [practising, setPractising] = useState(false);

  useEffect(() => {
    setDrag({ x: 0, y: 0, dragging: false });
    setLeaving(null);
    setPractising(false);
  }, [card.id]);

  const onPointerDown = (e: React.PointerEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("button") || t.closest("a")) return;
    if (t.closest("[data-no-drag]")) return;
    elRef.current?.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDrag((d) => ({ ...d, dragging: true }));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!startRef.current) return;
    setDrag({
      x: e.clientX - startRef.current.x,
      y: (e.clientY - startRef.current.y) * 0.25,
      dragging: true,
    });
  };
  const endDrag = () => {
    if (!startRef.current) return;
    const { x } = drag;
    startRef.current = null;
    const threshold = 110;
    if (Math.abs(x) > threshold) {
      setLeaving(x > 0 ? "right" : "left");
      window.setTimeout(() => onSwiped(), 260);
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotate = drag.x * 0.04;
  const flyX = leaving === "right" ? 800 : leaving === "left" ? -800 : drag.x;
  const flyRot = leaving ? (leaving === "right" ? 20 : -20) : rotate;
  const transition = drag.dragging
    ? "none"
    : "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms";
  const opacity = leaving ? 0 : 1;

  const onShare = async () => {
    const text = `${card.title} — ${card.quickReminder}\n\nFrom Recovery Trauma™ DBT Skills Cards.`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ text, title: `${card.title} · DBT Skill` });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      }
    } catch {
      /* dismissed */
    }
  };

  const cat = DBT_CATEGORIES.find((c) => c.id === card.category)!;
  const swipeHint =
    Math.abs(drag.x) > 30 ? (drag.x > 0 ? "right" : "left") : null;

  return (
    <div
      ref={elRef}
      role="group"
      aria-label={`${card.title} DBT skill card. Drag left or right for next.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="relative z-10 rounded-[28px] border border-gold/25 bg-gradient-to-br from-gold-light/40 via-card to-cream/60 p-6 shadow-[var(--shadow-gold)] select-none sm:p-7"
      style={{
        transform: `translate(${flyX}px, ${drag.y}px) rotate(${flyRot}deg)`,
        transition,
        opacity,
        cursor: drag.dragging ? "grabbing" : "grab",
        touchAction: "pan-y",
      }}
    >
      {swipeHint && (
        <span
          aria-hidden
          className={`pointer-events-none absolute top-6 rounded-full border-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest ${
            swipeHint === "right"
              ? "end-6 rotate-12 border-[var(--gold-deep)] text-[var(--gold-deep)]"
              : "start-6 -rotate-12 border-foreground/40 text-foreground/40"
          }`}
        >
          Next
        </span>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
          <span aria-hidden>{cat.emoji}</span>
          {cat.label}
        </span>
        <span className="text-2xl" aria-hidden>
          {card.emoji}
        </span>
      </div>

      <h2 className="mt-3 font-display text-3xl text-foreground sm:text-[34px]">
        {card.title}
      </h2>

      <div
        data-no-drag
        className="mt-4 max-h-[52vh] space-y-4 overflow-y-auto pe-1 text-sm leading-relaxed text-foreground/80"
      >
        <Section title="What Is It?">
          <p>{card.whatIsIt}</p>
        </Section>

        <Section title="When Might It Help?">
          <ul className="space-y-1.5">
            {card.whenItHelps.map((w) => (
              <li key={w} className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-[var(--gold-deep)]" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="How To Practise It">
          <ol className="space-y-1.5">
            {card.howToPractise.map((step, i) => (
              <li key={step} className="flex gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section title="Real-Life Examples">
          <ScenarioPicker
            key={card.id}
            skillId={card.id}
            skillTitle={card.title}
            scenarios={card.examples}
          />
        </Section>



        <div className="rounded-2xl border border-gold/30 bg-background/50 p-3 text-center text-[13px] italic text-foreground/80">
          “{card.quickReminder}”
        </div>

        <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-light/40 to-cream/60 p-4">
          <button
            type="button"
            onClick={() => setPractising((p) => !p)}
            className="flex w-full items-center justify-between gap-2 text-start text-sm font-semibold text-foreground"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[var(--gold-deep)]" />
              💛 Try This Skill Now
            </span>
            <span className="text-xs font-normal text-foreground/60">
              {practising ? "Hide" : "Open"}
            </span>
          </button>

          {practising && (
            <div className="mt-3 space-y-2 text-sm text-foreground/80">
              <p className="font-medium text-foreground">
                {card.practice.title}
              </p>
              <ol className="space-y-1.5">
                {card.practice.steps.map((step, i) => (
                  <li key={step} className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border border-[var(--gold-deep)] text-[10px] font-semibold text-[var(--gold-deep)]">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <p className="pt-1 text-[11px] text-foreground/55">
                Go gently. One breath at a time is enough.
              </p>
            </div>
          )}
        </div>

        {card.id === "stop" && <StopSelfCheck />}

      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => toggle({ label: card.title, sub: cat.label })}
          aria-pressed={saved}
          aria-label={saved ? "Remove from favourites" : "Save to favourites"}
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${
            saved
              ? "border-transparent bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
              : "border-gold/40 bg-background/60 text-[var(--gold-deep)] hover:bg-gold-light/40"
          }`}
        >
          <Heart
            className={`h-5 w-5 ${saved ? "fill-current" : ""}`}
            strokeWidth={2.25}
          />
        </button>

        <button
          type="button"
          onClick={() => onSwiped()}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90"
        >
          Next skill
        </button>

        <button
          type="button"
          onClick={onShare}
          aria-label="Share this skill"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-background/60 text-[var(--gold-deep)] hover:bg-gold-light/40"
        >
          {copied ? <Check className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

const STOP_NOTICE = [
  { id: "urge", label: "An urge to react", emoji: "⚡" },
  { id: "anger", label: "Anger or frustration", emoji: "🔥" },
  { id: "fear", label: "Fear or anxiety", emoji: "🌀" },
  { id: "sadness", label: "Sadness or heaviness", emoji: "💧" },
  { id: "shutdown", label: "Numbness / shutdown", emoji: "🌫️" },
  { id: "body", label: "Tension in my body", emoji: "🫀" },
] as const;

const STOP_NEXT = [
  { id: "breath", label: "Take three slow breaths", emoji: "🌬️" },
  { id: "walk", label: "Step away for a moment", emoji: "🚶" },
  { id: "water", label: "Sip water / cool my face", emoji: "💧" },
  { id: "wait", label: "Wait before replying", emoji: "⏳" },
  { id: "name", label: "Name what I'm feeling", emoji: "🏷️" },
  { id: "reach", label: "Reach out to someone safe", emoji: "🤝" },
] as const;

function ScenarioPicker({
  skillId,
  skillTitle,
  scenarios,
}: {
  skillId: string;
  skillTitle: string;
  scenarios: DbtScenario[];
}) {
  const [i, setI] = useState(0);
  const [journaling, setJournaling] = useState(false);

  useEffect(() => {
    setJournaling(false);
    setI(0);
  }, [skillId]);

  if (!scenarios.length) return null;
  const current = scenarios[Math.min(i, scenarios.length - 1)];

  return (
    <div data-no-drag className="space-y-2.5">
      <div className="flex flex-wrap gap-1.5">
        {scenarios.map((s, idx) => {
          const on = idx === i;
          return (
            <button
              key={s.context}
              type="button"
              onClick={() => {
                setI(idx);
                setJournaling(false);
              }}
              aria-pressed={on}
              className="rt-choice inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium"
            >
              <span aria-hidden>{s.emoji}</span>
              {s.context}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 rounded-2xl border border-gold/25 bg-background/60 p-3">
        <span aria-hidden className="mt-0.5">💡</span>
        <p className="text-sm leading-relaxed text-foreground/80">
          {current.text}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-foreground/50">
          Tap a chip to see how this skill lands in a different situation.
        </p>
        <button
          type="button"
          onClick={() => setJournaling((j) => !j)}
          className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-cream/50 px-2.5 py-1 text-[11px] font-semibold text-[var(--gold-deep)] hover:bg-gold-light/50"
        >
          <NotebookPen className="h-3 w-3" />
          {journaling ? "Close journal" : "Journal this"}
        </button>
      </div>

      {journaling && (
        <JournalTemplate
          skillId={skillId}
          skillTitle={skillTitle}
          scenario={current}
          onClose={() => setJournaling(false)}
        />
      )}
    </div>
  );
}

function JournalTemplate({
  skillId,
  skillTitle,
  scenario,
  onClose,
}: {
  skillId: string;
  skillTitle: string;
  scenario: DbtScenario;
  onClose: () => void;
}) {
  const { save } = useDbtJournal();
  const draftId = `draft:${skillId}:${scenario.context}`;

  const [situation, setSituation] = useState(scenario.text);
  const [noticed, setNoticed] = useState("");
  const [before, setBefore] = useState(6);
  const [after, setAfter] = useState(4);
  const [tried, setTried] = useState("");
  const [shifted, setShifted] = useState("");
  const [note, setNote] = useState("");
  const [bookmark, setBookmark] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Local draft persistence per skill+scenario so the template survives navigation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(draftId);
      if (raw) {
        const d = JSON.parse(raw);
        setSituation(d.situation ?? scenario.text);
        setNoticed(d.noticed ?? "");
        setBefore(d.before ?? 6);
        setAfter(d.after ?? 4);
        setTried(d.tried ?? "");
        setShifted(d.shifted ?? "");
        setNote(d.note ?? "");
        setBookmark(!!d.bookmark);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        draftId,
        JSON.stringify({ situation, noticed, before, after, tried, shifted, note, bookmark }),
      );
    } catch {
      /* ignore */
    }
  }, [draftId, situation, noticed, before, after, tried, shifted, note, bookmark]);

  const canSave = noticed.trim() || tried.trim() || shifted.trim() || note.trim();

  const onSave = () => {
    save({
      skillId,
      skillTitle,
      context: scenario.context,
      situation: situation.trim(),
      noticed: noticed.trim(),
      intensityBefore: before,
      intensityAfter: after,
      tried: tried.trim(),
      shifted: shifted.trim(),
      noteToSelf: note.trim(),
      bookmarked: bookmark,
    });
    setSavedAt(Date.now());
    window.setTimeout(() => setSavedAt(null), 2200);
  };

  return (
    <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-cream/60 to-background/70 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-4 w-4 text-[var(--gold-deep)]" />
          <h4 className="text-sm font-semibold text-foreground">
            One-page journal · {skillTitle}
          </h4>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close journal"
          className="rounded-full p-1 text-foreground/50 hover:bg-background/60 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="mb-3 text-[11px] text-foreground/60">
        <span className="font-medium">Context:</span> {scenario.emoji} {scenario.context}
        <span className="mx-1">·</span>
        <span className="italic">Saved to this device.</span>
      </p>

      <div className="space-y-3 text-sm">
        <JField label="The situation">
          <textarea
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            rows={2}
            className="w-full rounded-xl border border-gold/25 bg-background/70 p-2 text-sm text-foreground focus:border-[var(--gold-deep)] focus:outline-none"
          />
        </JField>

        <JField label="What I noticed (body, thoughts, feelings)">
          <textarea
            value={noticed}
            onChange={(e) => setNoticed(e.target.value)}
            rows={3}
            placeholder="Tight chest, racing thoughts, the urge to walk away…"
            className="w-full rounded-xl border border-gold/25 bg-background/70 p-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-[var(--gold-deep)] focus:outline-none"
          />
        </JField>

        <div className="grid grid-cols-2 gap-3">
          <IntensityField
            label="Intensity before"
            value={before}
            onChange={setBefore}
          />
          <IntensityField
            label="Intensity after"
            value={after}
            onChange={setAfter}
          />
        </div>

        <JField label="What I tried">
          <textarea
            value={tried}
            onChange={(e) => setTried(e.target.value)}
            rows={2}
            placeholder={`How I used ${skillTitle} — even one small step counts.`}
            className="w-full rounded-xl border border-gold/25 bg-background/70 p-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-[var(--gold-deep)] focus:outline-none"
          />
        </JField>

        <JField label="What shifted">
          <textarea
            value={shifted}
            onChange={(e) => setShifted(e.target.value)}
            rows={2}
            placeholder="Even a small softening is real."
            className="w-full rounded-xl border border-gold/25 bg-background/70 p-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-[var(--gold-deep)] focus:outline-none"
          />
        </JField>

        <JField label="A kind note to myself">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="You showed up. That matters."
            className="w-full rounded-xl border border-gold/25 bg-background/70 p-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-[var(--gold-deep)] focus:outline-none"
          />
        </JField>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <label className="inline-flex items-center gap-2 text-xs text-foreground/70">
            <input
              type="checkbox"
              checked={bookmark}
              onChange={(e) => setBookmark(e.target.checked)}
              className="h-4 w-4 accent-[var(--gold-deep)]"
            />
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
              Bookmark this entry
            </span>
          </label>

          <div className="flex items-center gap-2">
            {savedAt && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[var(--gold-deep)]">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Save entry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function JField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function IntensityField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
          {label}
        </span>
        <span className="text-xs font-semibold text-foreground">{value}/10</span>
      </div>
      <input
        type="range"
        min={0}
        max={10}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--gold-deep)]"
      />
    </div>
  );
}


function StopSelfCheck() {
  const [notice, setNotice] = useState<string[]>([]);
  const [intensity, setIntensity] = useState(5);
  const [next, setNext] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const toggleNotice = (id: string) => {
    setNotice((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const reset = () => {
    setNotice([]);
    setIntensity(5);
    setNext(null);
    setDone(false);
  };

  const noticeLabels = STOP_NOTICE.filter((n) => notice.includes(n.id))
    .map((n) => n.label.toLowerCase());
  const nextLabel = STOP_NEXT.find((n) => n.id === next)?.label.toLowerCase();

  return (
    <div
      data-no-drag
      className="rounded-2xl border border-gold/30 bg-gradient-to-br from-background/70 to-cream/50 p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <span aria-hidden className="text-lg">🛑</span>
        <h3 className="text-sm font-semibold text-foreground">
          STOP self-check
        </h3>
      </div>

      {done ? (
        <div className="space-y-3 text-sm text-foreground/80">
          <p className="rounded-xl bg-background/70 p-3 leading-relaxed">
            Right now you're noticing{" "}
            <span className="font-medium text-foreground">
              {noticeLabels.length ? noticeLabels.join(", ") : "something"}
            </span>
            , at intensity{" "}
            <span className="font-medium text-foreground">{intensity}/10</span>.
            Your next kind step:{" "}
            <span className="font-medium text-foreground">
              {nextLabel ?? "one gentle breath"}
            </span>
            .
          </p>
          <p className="text-[12px] italic text-foreground/70">
            You just paused. That's the whole skill. 💛
          </p>
          <button
            type="button"
            onClick={reset}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/60 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)] hover:bg-gold-light/40"
          >
            Start again
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
              1. What do you notice?
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STOP_NOTICE.map((n) => {
                const on = notice.includes(n.id);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => toggleNotice(n.id)}
                    aria-pressed={on}
                    className="rt-choice inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs"
                  >
                    <span aria-hidden>{n.emoji}</span>
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
                2. How intense does it feel?
              </p>
              <span className="text-xs font-semibold text-foreground">
                {intensity}/10
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              value={intensity}
              onChange={(e) => setIntensity(Number(e.target.value))}
              aria-label="Intensity from 1 to 10"
              className="w-full accent-[var(--gold-deep)]"
            />
            <div className="mt-1 flex justify-between text-[10px] text-foreground/55">
              <span>gentle</span>
              <span>overwhelming</span>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
              3. What will you do next?
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {STOP_NEXT.map((n) => {
                const on = next === n.id;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setNext(n.id)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-start text-xs transition-colors ${
                      on
                        ? "border-transparent bg-foreground text-background"
                        : "border-gold/30 bg-background/60 text-foreground/75 hover:bg-gold-light/40"
                    }`}
                  >
                    <span aria-hidden>{n.emoji}</span>
                    <span>{n.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setDone(true)}
            disabled={notice.length === 0 || !next}
            className="w-full rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            See my pause
          </button>
        </div>
      )}
    </div>
  );
}


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function SavedJournalsDrawer({ onClose }: { onClose: () => void }) {
  const { entries, toggleBookmark, remove } = useDbtJournal();
  const [tab, setTab] = useState<"all" | "bookmarked">("all");
  const list = tab === "bookmarked" ? entries.filter((e) => e.bookmarked) : entries;

  return (
    <div className="mb-6 rounded-2xl border border-gold/30 bg-card p-4 shadow-[var(--shadow-gold)]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-4 w-4 text-[var(--gold-deep)]" />
          <h2 className="text-sm font-semibold text-foreground">
            Your DBT journals
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close saved journals"
          className="rounded-full p-1 text-foreground/50 hover:bg-background/60 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-3 flex gap-2">
        {(["all", "bookmarked"] as const).map((t) => {
          const on = tab === t;
          const n = t === "bookmarked" ? entries.filter((e) => e.bookmarked).length : entries.length;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-medium ${
                on
                  ? "border-transparent bg-foreground text-background"
                  : "border-gold/30 bg-background/60 text-foreground/70 hover:bg-gold-light/40"
              }`}
            >
              {t === "all" ? "All" : "Bookmarked"}
              <span className="opacity-70">· {n}</span>
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <p className="rounded-xl bg-background/60 p-4 text-center text-xs text-foreground/60">
          {tab === "bookmarked"
            ? "No bookmarked entries yet. Star an entry to keep it close."
            : "No journal entries yet. Tap “Journal this” on any skill to start."}
        </p>
      ) : (
        <ul className="space-y-2">
          {list.map((e) => (
            <SavedJournalItem
              key={e.id}
              entry={e}
              onToggleBookmark={() => toggleBookmark(e.id)}
              onRemove={() => remove(e.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SavedJournalItem({
  entry,
  onToggleBookmark,
  onRemove,
}: {
  entry: DbtJournalEntry;
  onToggleBookmark: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const when = new Date(entry.updatedAt).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <li className="rounded-2xl border border-gold/25 bg-background/60 p-3">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 text-start"
        >
          <p className="text-sm font-semibold text-foreground">
            {entry.skillTitle}{" "}
            <span className="text-foreground/50">· {entry.context}</span>
          </p>
          <p className="mt-0.5 text-[11px] text-foreground/55">
            {when} · intensity {entry.intensityBefore} → {entry.intensityAfter}
          </p>
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleBookmark}
            aria-pressed={entry.bookmarked}
            aria-label={entry.bookmarked ? "Remove bookmark" : "Bookmark"}
            className={`rounded-full p-1.5 ${
              entry.bookmarked
                ? "bg-[var(--gradient-gold)] text-foreground"
                : "text-[var(--gold-deep)] hover:bg-gold-light/40"
            }`}
          >
            <Star
              className={`h-4 w-4 ${entry.bookmarked ? "fill-current" : ""}`}
            />
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm("Delete this journal entry?")) onRemove();
            }}
            aria-label="Delete entry"
            className="rounded-full p-1.5 text-foreground/40 hover:bg-background/60 hover:text-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-2 border-t border-gold/20 pt-3 text-xs text-foreground/75">
          {entry.situation && (
            <JournalRow label="Situation">{entry.situation}</JournalRow>
          )}
          {entry.noticed && (
            <JournalRow label="What I noticed">{entry.noticed}</JournalRow>
          )}
          {entry.tried && (
            <JournalRow label="What I tried">{entry.tried}</JournalRow>
          )}
          {entry.shifted && (
            <JournalRow label="What shifted">{entry.shifted}</JournalRow>
          )}
          {entry.noteToSelf && (
            <JournalRow label="Note to self">{entry.noteToSelf}</JournalRow>
          )}
        </div>
      )}
    </li>
  );
}

function JournalRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
        {label}
      </p>
      <p className="mt-0.5 whitespace-pre-wrap leading-relaxed">{children}</p>
    </div>
  );
}

